import { useState, useMemo } from 'react';
import {
  DataGridProProps,
  GridColumns,
  GridRowSpacingParams,
  GridSelectionModel,
  GridValidRowModel,
  GridRowIdGetter,
  GridSortDirection
} from '@mui/x-data-grid-pro';

import { EIcons, Icon } from 'ui';

const getRowSpacing = (params: GridRowSpacingParams) => ({
  top: params.isFirstVisible ? 12 : 6,
  bottom: params.isLastVisible ? 0 : 6
});

const rowsPerPageOptions = [25, 50, 100] as const;

const groupingIdPrefix = 'group-child-';

export type SortModel<T extends object> = {
  field: keyof T | '__tree_data_group__';
  sort: GridSortDirection;
}[];

type Options<T> = {
  getRowId?: GridRowIdGetter<T>;
  getPath?: (row: T) => string[];
  readonly rowsPerPageOptions?: number[];
  initialRowsPerPage?: number;
};

export function useServerSideDataGrid<T extends GridValidRowModel = any>(
  columns: GridColumns<T>,
  options?: Options<T>
) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(
    options?.initialRowsPerPage ?? options?.rowsPerPageOptions?.[0] ?? rowsPerPageOptions[0]
  );
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [sortModel, setSortModel] = useState<SortModel<T>>([]);

  const selectedRowIds = useMemo(() => {
    const ids: string[][] = [];
    selectionModel.forEach(id => {
      const idAsString = id.toString();
      if (idAsString.startsWith(groupingIdPrefix)) {
        ids.push(idAsString.replace(groupingIdPrefix, '').split(','));
        return;
      }
      ids.push(idAsString.split(','));
    });
    return ids;
  }, [selectionModel]);

  const getRowIdWithPath = (row: T) => {
    if (options?.getPath) {
      const path = options.getPath(row);
      return path?.length > 1 ? `${groupingIdPrefix}${path}` : path.toString();
    }
    if (options?.getRowId) {
      return options.getRowId(row);
    }
    return '';
  };

  const onSelectionModelChange = (newSelectionModel: GridSelectionModel) => {
    setSelectionModel(newSelectionModel);
  };

  const clearSelection = () => {
    setSelectionModel([]);
  };

  const changePage = (newPage: number) => {
    setPage(newPage);
  };

  const dataGridProps: Omit<DataGridProProps<T>, 'rows'> = {
    columns,
    page,
    onPageChange: setPage,
    sortingMode: 'server',
    paginationMode: 'server',
    selectionModel,
    onSelectionModelChange,
    onSortModelChange: setSortModel,
    onPageSizeChange: setPageSize as DataGridProProps<T>['onPageSizeChange'],
    getRowSpacing,
    pagination: true,
    pageSize,
    getRowId: getRowIdWithPath,
    // changeable props
    disableSelectionOnClick: true,
    disableColumnMenu: true,
    keepNonExistentRowsSelected: true,
    components: {
      MoreActionsIcon: props => <Icon name={EIcons.moreActions} {...props} />
    },
    rowsPerPageOptions: (options?.rowsPerPageOptions ?? rowsPerPageOptions) as number[],
    checkboxSelection: true
  };

  return { dataGridProps, page, pageSize, selectionModel, sortModel, selectedRowIds, clearSelection, changePage };
}
